import { useState, useEffect, useRef } from "react";
import apiFetch from "../../../../shared/services/apiClient";

const POLL_MS = 3000;

/**
 * Modal para crear Multas / Sanciones sobre filiales seleccionadas.
 *
 * Props:
 *   selectedIds  — array de { filialId, filialLabel, propietario }
 *   onClose()    — cerrar modal
 *   onDone()     — callback al terminar (para refrescar el dashboard)
 */
export default function MultaModal({ selectedIds = [], onClose, onDone }) {
  // Fecha hoy por defecto
  const today = new Date().toISOString().slice(0, 10);
  const thirtyDays = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);

  const [concepto,     setConcepto]     = useState("Multa por incumplimiento de reglamento");
  const [monto,        setMonto]        = useState("");
  const [fechaEmision, setFechaEmision] = useState(today);
  const [fechaVenc,    setFechaVenc]    = useState(thirtyDays);
  const [cuentas,      setCuentas]      = useState([]);
  const [cuentaId,     setCuentaId]     = useState("");
  const [enviarEmail,  setEnviarEmail]  = useState(true);
  const [idioma,       setIdioma]       = useState("es");
  const [loading,      setLoading]      = useState(false);
  const [taskId,       setTaskId]       = useState(null);
  const [result,       setResult]       = useState(null);
  const [error,        setError]        = useState("");
  const pollRef = useRef(null);

  // Cargar cuentas ingreso disponibles
  useEffect(() => {
    apiFetch("/api/finance/catalogo/?tipo=INGRESO&is_detalle=true")
      .then((r) => r.json())
      .then((data) => {
        const list = data.results ?? data;
        setCuentas(Array.isArray(list) ? list : []);
      })
      .catch(() => {});
  }, []);

  // Polling de estado de email
  useEffect(() => {
    if (!taskId) return;
    pollRef.current = setInterval(async () => {
      try {
        const res  = await apiFetch(`/api/finance/factura/email-status/?task_id=${taskId}`);
        const data = await res.json();
        if (data.done) {
          clearInterval(pollRef.current);
          setTaskId(null);
          setLoading(false);
          setResult((prev) => ({ ...prev, email: data }));
          onDone?.();
        }
      } catch { /* ignore */ }
    }, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [taskId]);

  async function handleCrear() {
    if (!monto || isNaN(Number(monto)) || Number(monto) <= 0) {
      setError("Ingrese un monto válido mayor a cero.");
      return;
    }
    if (!concepto.trim()) {
      setError("El concepto es obligatorio.");
      return;
    }
    if (selectedIds.length === 0) {
      setError("No hay filiales seleccionadas.");
      return;
    }
    setError("");
    setLoading(true);

    try {
      // Paso 1: crear las multas
      const res  = await apiFetch("/api/finance/factura/crear-multa/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          filial_ids:         selectedIds.map((x) => x.filialId),
          monto:              Number(monto),
          concepto:           concepto.trim(),
          fecha_emision:      fechaEmision,
          fecha_vencimiento:  fechaVenc,
          cuenta_ingreso_id:  cuentaId || null,
          enviar_email:       enviarEmail,
          idioma,
        }),
      });
      const data = await res.json();

      if (!res.ok && !data.creadas?.length) {
        setError(data.error || "Error al crear las multas.");
        setLoading(false);
        return;
      }

      setResult({ multa: data });

      // Paso 2: enviar emails si se solicitó
      if (enviarEmail && data.fac_ids_email?.length > 0) {
        try {
          const resEmail = await apiFetch("/api/finance/factura/enviar-email/", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ factura_ids: data.fac_ids_email, idioma }),
          });
          const dataEmail = await resEmail.json();
          if (resEmail.ok && dataEmail.task_id) {
            setTaskId(dataEmail.task_id);
            return; // polling will finish and call setLoading(false)
          }
        } catch { /* email error non-fatal */ }
      }

      setLoading(false);
      onDone?.();

    } catch (e) {
      setError("Error de red: " + e.message);
      setLoading(false);
    }
  }

  const inputStyle = {
    width: "100%", padding: "8px 10px",
    background: "var(--bg-input)", border: "1px solid var(--border-card)",
    borderRadius: 6, color: "var(--text-primary)", fontSize: "0.85rem",
    colorScheme: "light dark", outline: "none",
    boxSizing: "border-box", transition: "border-color 0.15s",
  };
  const sectionLabel = {
    fontSize: "0.67rem", fontWeight: 700, letterSpacing: "1.1px",
    textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 8,
  };

  const multa      = result?.multa;
  const emailResult = result?.email;
  const done = multa && (!enviarEmail || emailResult || !taskId);

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)",
        backdropFilter: "blur(5px)", zIndex: 1000,
      }} />

      <div onClick={(e) => e.stopPropagation()} style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%,-50%)",
        background: "var(--bg-card)", border: "1px solid var(--border-card)",
        borderRadius: 16, width: "min(540px,95vw)", zIndex: 1001,
        boxShadow: "0 32px 80px rgba(0,0,0,0.65)", overflow: "hidden",
        maxHeight: "92vh", display: "flex", flexDirection: "column",
      }}>
        {/* Header */}
        <div style={{
          background: "linear-gradient(135deg,rgba(248,113,113,0.13) 0%,rgba(248,113,113,0.04) 100%)",
          borderBottom: "1px solid var(--border-card)", padding: "18px 22px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 13 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 11,
              background: "rgba(248,113,113,0.14)", border: "1px solid rgba(248,113,113,0.28)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <i className="fa-solid fa-gavel" style={{ color: "#f87171", fontSize: "1.1rem" }} />
            </div>
            <div>
              <div style={{ fontSize: "1rem", fontWeight: 700, color: "var(--text-primary)", lineHeight: 1.2 }}>
                Multa / Sanción
              </div>
              <div style={{ fontSize: "0.74rem", color: "var(--text-muted)", marginTop: 2 }}>
                {selectedIds.length} filial{selectedIds.length !== 1 ? "es" : ""} seleccionada{selectedIds.length !== 1 ? "s" : ""}
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: "none", border: "none", cursor: "pointer",
            color: "var(--text-muted)", fontSize: "1.1rem", padding: "4px 6px",
          }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14, overflowY: "auto" }}>

          {/* Filiales */}
          <div>
            <div style={sectionLabel}>Filiales</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 72, overflowY: "auto" }}>
              {selectedIds.map((s) => (
                <span key={s.filialId} style={{
                  padding: "3px 10px", borderRadius: 12, fontSize: "0.76rem",
                  background: "rgba(248,113,113,0.12)", border: "1px solid rgba(248,113,113,0.25)",
                  color: "#f87171", fontWeight: 600,
                }}>
                  {s.filialLabel}
                  {s.propietario ? ` · ${s.propietario.split(" ")[0]}` : ""}
                </span>
              ))}
            </div>
          </div>

          {/* Concepto */}
          <div>
            <div style={sectionLabel}>Concepto de la multa</div>
            <input
              type="text"
              value={concepto}
              onChange={(e) => setConcepto(e.target.value)}
              placeholder="Ej: Multa por ruido excesivo"
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = "#f87171")}
              onBlur={(e)  => (e.target.style.borderColor = "var(--border-card)")}
            />
          </div>

          {/* Monto */}
          <div>
            <div style={sectionLabel}>Monto (₡)</div>
            <input
              type="number"
              min="0"
              step="500"
              value={monto}
              onChange={(e) => setMonto(e.target.value)}
              placeholder="0.00"
              style={{ ...inputStyle, textAlign: "right" }}
              onFocus={(e) => (e.target.style.borderColor = "#f87171")}
              onBlur={(e)  => (e.target.style.borderColor = "var(--border-card)")}
            />
          </div>

          {/* Fechas */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <div style={sectionLabel}>Fecha de emisión</div>
              <input type="date" value={fechaEmision}
                onChange={(e) => setFechaEmision(e.target.value)}
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = "#f87171")}
                onBlur={(e)  => (e.target.style.borderColor = "var(--border-card)")}
              />
            </div>
            <div>
              <div style={sectionLabel}>Fecha de vencimiento</div>
              <input type="date" value={fechaVenc}
                onChange={(e) => setFechaVenc(e.target.value)}
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = "#f87171")}
                onBlur={(e)  => (e.target.style.borderColor = "var(--border-card)")}
              />
            </div>
          </div>

          {/* Cuenta ingreso (opcional) */}
          {cuentas.length > 0 && (
            <div>
              <div style={sectionLabel}>Cuenta contable (opcional)</div>
              <select
                value={cuentaId}
                onChange={(e) => setCuentaId(e.target.value)}
                style={{ ...inputStyle, cursor: "pointer" }}
              >
                <option value="">— Sin cuenta contable —</option>
                {cuentas.map((c) => (
                  <option key={c.id} value={c.id} style={{ background: "var(--bg-card)" }}>
                    {c.codigo ? `${c.codigo} · ` : ""}{c.nombre}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div style={{ borderTop: "1px solid var(--border-card)" }} />

          {/* Enviar email */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input
              type="checkbox"
              id="multa-email"
              checked={enviarEmail}
              onChange={(e) => setEnviarEmail(e.target.checked)}
              style={{ width: 16, height: 16, cursor: "pointer", accentColor: "#f87171" }}
            />
            <label htmlFor="multa-email" style={{ fontSize: "0.85rem", color: "var(--text-primary)", cursor: "pointer" }}>
              Notificar al residente por correo electrónico
            </label>
          </div>

          {enviarEmail && (
            <div>
              <div style={sectionLabel}>Idioma del correo</div>
              <div style={{
                display: "flex", borderRadius: 9,
                border: "1px solid var(--border-card)", overflow: "hidden",
              }}>
                {[
                  { key: "es", label: "🇨🇷 Español" },
                  { key: "en", label: "🇺🇸 English" },
                ].map((opt, i) => (
                  <button key={opt.key} onClick={() => setIdioma(opt.key)} style={{
                    flex: 1, padding: "9px 0", border: "none",
                    borderRight: i === 0 ? "1px solid var(--border-card)" : "none",
                    background: idioma === opt.key ? "rgba(248,113,113,0.12)" : "transparent",
                    color: idioma === opt.key ? "#f87171" : "var(--text-muted)",
                    fontWeight: idioma === opt.key ? 700 : 400,
                    fontSize: "0.85rem", cursor: "pointer",
                  }}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{
              background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.3)",
              borderLeft: "3px solid #f87171", borderRadius: 6, padding: "8px 12px",
              color: "#fca5a5", fontSize: "0.82rem",
            }}>
              <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 5 }} />
              {error}
            </div>
          )}

          {/* Resultado */}
          {multa && (
            <div style={{
              background: "rgba(74,222,128,0.08)", border: "1px solid rgba(74,222,128,0.25)",
              borderRadius: 8, padding: "12px 16px",
            }}>
              <p style={{ margin: "0 0 4px", fontWeight: 700, color: "#4ade80", fontSize: "0.87rem" }}>
                <i className="fa-solid fa-circle-check" style={{ marginRight: 6 }} />
                Multas creadas: <strong>{multa.total_creadas}</strong>
                {multa.total_errores > 0 && (
                  <span style={{ color: "#f87171", marginLeft: 8 }}>
                    · Errores: {multa.total_errores}
                  </span>
                )}
              </p>
              {enviarEmail && !emailResult && taskId && (
                <p style={{ margin: "6px 0 0", fontSize: "0.79rem", color: "var(--text-muted)" }}>
                  <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 5 }} />
                  Enviando notificaciones por correo…
                </p>
              )}
              {emailResult && (
                <p style={{ margin: "6px 0 0", fontSize: "0.79rem", color: "var(--text-muted)" }}>
                  Correos enviados: <strong style={{ color: "#4ade80" }}>{emailResult.total_enviados}</strong>
                  {emailResult.total_errores > 0 && (
                    <> · Errores email: <strong style={{ color: "#f87171" }}>{emailResult.total_errores}</strong></>
                  )}
                </p>
              )}
              {multa.errores?.length > 0 && (
                <ul style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: "0.76rem", color: "#f87171" }}>
                  {multa.errores.map((e, i) => (
                    <li key={i}>{e.filial}: {e.error}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          borderTop: "1px solid var(--border-card)", background: "var(--hover-bg)",
          padding: "14px 24px", display: "flex", justifyContent: "flex-end", gap: 10,
          flexShrink: 0,
        }}>
          <button onClick={() => { onClose(); if (done) onDone?.(); }}
            disabled={loading && !!taskId}
            style={{
              padding: "9px 18px", background: "transparent",
              border: "1px solid var(--border-card)", borderRadius: 8,
              color: "var(--text-muted)", fontSize: "0.85rem", cursor: "pointer",
            }}>
            {done ? "Cerrar" : "Cancelar"}
          </button>
          {!multa && (
            <button onClick={handleCrear} disabled={loading || selectedIds.length === 0}
              style={{
                padding: "9px 22px",
                background: loading ? "rgba(248,113,113,0.6)" : "#f87171",
                color: "#fff", border: "none", borderRadius: 8,
                fontWeight: 700, fontSize: "0.85rem",
                cursor: loading || selectedIds.length === 0 ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", gap: 8,
                boxShadow: loading ? "none" : "0 4px 16px rgba(248,113,113,0.38)",
                opacity: selectedIds.length === 0 ? 0.5 : 1,
              }}
              onMouseOver={(e) => { if (!loading) e.currentTarget.style.background = "#dc2626"; }}
              onMouseOut={(e)  => { if (!loading) e.currentTarget.style.background = "#f87171"; }}
            >
              {loading ? (
                <><i className="fa-solid fa-circle-notch fa-spin" /> Creando…</>
              ) : (
                <><i className="fa-solid fa-gavel" /> Aplicar Multa</>
              )}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
